import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  ArrowLeft,
  Upload,
  Calendar,
  Building2,
  Package,
  Search,
  Download,
  X,
  CheckCircle2,
  FileSpreadsheet,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import type {
  Depot,
  InventoryItem,
} from './types';
import { getDeposAPI } from '../../api/locations';
import { getProducts } from '../../api/products';
import { CustomDropdown } from '../../components/Common/CustomDropdown';
import { CustomCalendar } from '../../components/Common/CustomCalendar';
import {
  getStockRegisterAPI,
  downloadInventoryTemplateAPI,
  uploadInventoryBulkAPI,
  getDepoStockAPI,
} from '../../api/inventory';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface PreUploadItem {
  rowNumber: number;
  depotName: string;
  sapCode: string;
  productName: string;
  batchNumber: string;
  quantity: number;
  crates: number;
  mfgDate: string;
  expiryDate: string;
  mrp: number;
}

export const InventoryManagement: React.FC = () => {
  // 1. Core State
  const [depos, setDepos] = useState<Depot[]>([]);
  const [selectedDepotId, setSelectedDepotId] = useState<number | 'all'>('all');
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown options lists loaded from APIs
  const [deposList, setDeposList] = useState<any[]>([]);
  const [productsList, setProductsList] = useState<any[]>([]);

  // 2. Data state
  const [stockItems, setStockItems] = useState<InventoryItem[]>([]);
  const [loadingStock, setLoadingStock] = useState<boolean>(false);

  // 3. Simple Upload Modal & Pre-Upload Preview State
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<PreUploadItem[]>([]);
  const [parsingFile, setParsingFile] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const formatDateStr = (dateVal?: string | null) => {
    if (!dateVal || dateVal === 'N/A') return 'N/A';
    if (dateVal.includes('T')) {
      return dateVal.split('T')[0];
    }
    return dateVal;
  };

  const formatDateTimeStr = (dateVal?: string | null) => {
    if (!dateVal || dateVal === 'N/A') return 'N/A';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return dateVal;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const timeStr = d.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      return `${yyyy}-${mm}-${dd} ${timeStr}`;
    } catch {
      return dateVal;
    }
  };

  // Parse Excel / CSV File for Live Pre-Upload Preview
  const handleFileSelect = async (file: File | null) => {
    setUploadFile(file);
    setParseError(null);
    setPreviewRows([]);

    if (!file) return;

    setParsingFile(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        throw new Error('No worksheets found in this file.');
      }
      const worksheet = workbook.Sheets[firstSheetName];
      const rawRows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (!rawRows || rawRows.length < 2) {
        throw new Error('The file appears to be empty or missing data rows.');
      }

      const headers = (rawRows[0] || []).map((h) => String(h || '').trim().toLowerCase());

      const findIdx = (keywords: string[], fallback: number) => {
        const idx = headers.findIndex((h) => keywords.some((k) => h.includes(k)));
        return idx >= 0 ? idx : fallback;
      };

      const depotIdx = findIdx(['depot', 'depo'], 0);
      const sapIdx = findIdx(['sap', 'sku', 'code'], 1);
      const productIdx = findIdx(['product', 'name', 'item'], 2);
      const batchIdx = findIdx(['batch'], 3);
      const qtyIdx = findIdx(['quantity', 'qty', 'unit'], 4);
      const cratesIdx = findIdx(['crate'], 5);
      const mfgIdx = findIdx(['mfg', 'manufacture'], 6);
      const expIdx = findIdx(['expiry', 'exp'], 7);
      const mrpIdx = findIdx(['mrp', 'price'], 8);

      const parsed: PreUploadItem[] = [];

      for (let i = 1; i < rawRows.length; i++) {
        const row = rawRows[i];
        if (!row || row.length === 0 || row.every((c) => c === undefined || c === null || String(c).trim() === '')) {
          continue;
        }

        const qty = Number(row[qtyIdx] || 0);
        const crates = row[cratesIdx] !== undefined ? Number(row[cratesIdx] || 0) : Math.ceil(qty / 12);

        const formatCellDate = (val: any) => {
          if (!val) return 'N/A';
          if (typeof val === 'number') {
            const date = new Date(Math.round((val - 25569) * 86400 * 1000));
            return isNaN(date.getTime()) ? String(val) : date.toISOString().split('T')[0];
          }
          const str = String(val).trim();
          if (str.includes('T')) return str.split('T')[0];
          return str;
        };

        parsed.push({
          rowNumber: parsed.length + 1,
          depotName: String(row[depotIdx] || 'Main Depot').trim(),
          sapCode: String(row[sapIdx] || '').trim(),
          productName: String(row[productIdx] || 'Product').trim(),
          batchNumber: String(row[batchIdx] || 'N/A').trim(),
          quantity: isNaN(qty) ? 0 : qty,
          crates: isNaN(crates) ? 0 : crates,
          mfgDate: formatCellDate(row[mfgIdx]),
          expiryDate: formatCellDate(row[expIdx]),
          mrp: Number(row[mrpIdx] || 0),
        });
      }

      if (parsed.length === 0) {
        throw new Error('No valid product rows could be extracted from this file.');
      }

      setPreviewRows(parsed);
    } catch (err: any) {
      console.warn('Pre-upload parse error:', err);
      setParseError(err?.message || 'Unable to parse file preview. You can still proceed to upload.');
    } finally {
      setParsingFile(false);
    }
  };

  // Load backend stock register & live depo stock
  const fetchStockRegister = useCallback(async () => {
    try {
      setLoadingStock(true);

      // If specific depot selected, fetch live depot stock via getDepoStockAPI
      if (selectedDepotId !== 'all') {
        try {
          const depoRes = await getDepoStockAPI(selectedDepotId, { search: searchQuery });
          const depoItems = depoRes?.data?.items;
          if (Array.isArray(depoItems) && depoItems.length > 0) {
            const mappedDepo: InventoryItem[] = depoItems.map((item: any, idx: number) => {
              const pSap = item.product?.sapCode || `SAP-${item.productId}`;
              const dObj = depos.find((d) => d.id === item.depoId);
              const dName = dObj?.name || `Depot ${item.depoId}`;
              const dSap = dObj?.sapCode || '';
              const dDisplay = dSap ? `${dName} (${dSap})` : dName;
              const added = formatDateTimeStr(item.createdAt || item.created_at || item.addedDate);

              return {
                id: String(item.productId || `DEPO-${idx}`),
                skuCode: pSap,
                sapCode: pSap,
                productName: item.product?.name || item.product?.displayName || 'Product',
                category: item.product?.category?.name || 'Dairy',
                packSize: '1L',
                unitType: 'Units',
                unitsPerCrate: 12,
                depotId: item.depoId,
                depotName: dDisplay,
                depotSapCode: dSap,
                batchNumber: 'N/A',
                mfgDate: 'N/A',
                expiryDate: 'N/A',
                earliestExpiry: 'N/A',
                openingStock: Number(item.totalQuantity || 0),
                inwardToday: 0,
                outwardToday: 0,
                damagedQty: 0,
                currentStock: Number(item.availableQuantity ?? item.totalQuantity ?? 0),
                currentCrates: Math.ceil(Number(item.availableQuantity ?? item.totalQuantity ?? 0) / 12),
                safetyStockLevel: 100,
                mrp: 0,
                activeBatches: 1,
                status: Number(item.totalQuantity || 0) > 0 ? 'healthy' : 'low',
                lastUpdated: 'Just now',
                createdAt: added,
                addedDate: added,
              };
            });
            setStockItems(mappedDepo);
          }
        } catch (err) {
          console.warn('Depo stock fetch fallback to stock register:', err);
        }
      }

      const res = await getStockRegisterAPI({
        depoId: selectedDepotId,
        date: selectedDate,
        product: selectedProduct,
        search: searchQuery,
      });
      const rawList = res?.data?.items || (res as any)?.data?.list || res?.data || (Array.isArray(res) ? res : null);
      if (Array.isArray(rawList) && rawList.length > 0) {
        const mapped: InventoryItem[] = rawList.map((item: any, idx: number) => {
          const productSapCode = item.product?.sapCode || item.sapCode || item.skuCode || item.sku || 'N/A';
          const depotNameRaw = item.depo?.name || item.depotName || 'Depot';
          const depoSapCode = item.depo?.sapCode || item.depotSapCode || item.depoSapCode || '';
          const fullDepotDisplay = depoSapCode ? `${depotNameRaw} (${depoSapCode})` : depotNameRaw;

          const mfg = formatDateStr(item.mfgDate || item.manufactureDate);
          const exp = formatDateStr(item.expiryDate || item.earliestExpiry);
          const added = formatDateTimeStr(item.createdAt || item.created_at || item.addedDate);

          return {
            id: String(item.id || item.batchNumber || `ITEM-${idx}`),
            skuCode: productSapCode,
            sapCode: productSapCode,
            productName: item.product?.name || item.product?.displayName || item.productName || item.name || 'Unnamed Product',
            category: item.category || item.productCategory || 'Dairy',
            packSize: item.packSize || item.size || '1L',
            unitType: item.unitType || item.unit || 'Jar',
            unitsPerCrate: item.unitsPerCrate || item.crateCapacity || 12,
            depotId: item.depoId || item.depo?.id || 1,
            depotName: fullDepotDisplay,
            depotSapCode: depoSapCode,
            batchNumber: item.batchNumber || 'N/A',
            mfgDate: mfg,
            expiryDate: exp,
            earliestExpiry: exp,
            openingStock: Number(item.openingStock ?? item.opening_stock ?? 0),
            inwardToday: Number(item.inwardToday ?? item.inward_today ?? 0),
            outwardToday: Number(item.outwardToday ?? item.outward_today ?? 0),
            damagedQty: Number(item.damagedQty ?? item.damaged_qty ?? 0),
            currentStock: Number(item.quantity ?? item.currentStock ?? 0),
            currentCrates: Math.ceil(Number(item.quantity ?? item.currentStock ?? 0) / (item.unitsPerCrate || 12)),
            safetyStockLevel: Number(item.safetyStockLevel ?? item.safety_stock_level ?? 100),
            mrp: Number(item.mrp ?? 0),
            activeBatches: 1,
            status: Number(item.quantity ?? item.currentStock ?? 0) > 0 ? 'healthy' : 'low',
            lastUpdated: item.updatedAt ? new Date(item.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now',
            createdAt: added,
            addedDate: added,
          };
        });
        setStockItems(mapped);
      } else {
        setStockItems([]);
      }
    } catch (err) {
      console.warn('Failed to fetch stock register API:', err);
      setStockItems([]);
    } finally {
      setLoadingStock(false);
    }
  }, [selectedDepotId, searchQuery, depos, selectedDate, selectedProduct]);

  useEffect(() => {
    fetchStockRegister();
  }, [fetchStockRegister]);

  // Load backend dropdown data (depos, products) from respective APIs
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [dRes, pRes] = await Promise.all([
          getDeposAPI().catch(() => ({ data: [] })),
          getProducts({ limit: 100 }).catch(() => ({ data: { products: [] } })),
        ]);

        const dData = dRes?.data || dRes?.depos || (Array.isArray(dRes) ? dRes : []);
        if (Array.isArray(dData)) {
          setDeposList(dData);
          const mapped: Depot[] = dData.map((d: any) => ({
            id: d.id,
            name: d.name,
            sapCode: d.sapCode || null,
            zone: d.zone?.name || 'WEST',
            city: d.cityName || d.name,
          }));
          setDepos(mapped);
        }

        const pData = (pRes as any)?.data?.products || (pRes as any)?.products || (pRes as any)?.data || (Array.isArray(pRes) ? pRes : []);
        if (Array.isArray(pData)) setProductsList(pData);
      } catch (err) {
        console.warn('Failed to fetch dropdown options:', err);
      }
    };
    fetchDropdownData();
  }, []);

  // Depots Options for CustomDropdown
  const depoOptions = useMemo(() => {
    const opts = [{ id: 'all', name: 'All Depots' }];
    deposList.forEach((d) => {
      const label = d.sapCode ? `${d.name} (${d.sapCode})` : d.name;
      opts.push({ id: d.id, name: label });
    });
    return opts;
  }, [deposList]);

  const selectedDepoName = useMemo(() => {
    if (selectedDepotId === 'all') return 'All Depots';
    const found = deposList.find((d) => d.id === selectedDepotId);
    if (found) {
      return found.sapCode ? `${found.name} (${found.sapCode})` : found.name;
    }
    return 'All Depots';
  }, [selectedDepotId, deposList]);

  // Products Options for CustomDropdown
  const productOptions = useMemo(() => {
    const opts = [{ id: 'all', name: 'All Products' }];
    if (productsList.length > 0) {
      productsList.forEach((p) => {
        const name = p.name || p.displayName || 'Product';
        const code = p.sku_code || p.sapCode || p.id;
        opts.push({ id: code, name });
      });
    } else {
      const map = new Map<string, string>();
      stockItems.forEach((item) => {
        if (!map.has(item.skuCode)) {
          map.set(item.skuCode, item.productName);
        }
      });
      map.forEach((productName, skuCode) => {
        opts.push({ id: skuCode, name: productName });
      });
    }
    return opts;
  }, [productsList, stockItems]);

  const selectedProductName = useMemo(() => {
    if (selectedProduct === 'all') return 'All Products';
    const found = productsList.find(
      (p) => p.sku_code === selectedProduct || p.sapCode === selectedProduct || p.name === selectedProduct
    );
    return found ? (found.name || found.displayName) : selectedProduct;
  }, [selectedProduct, productsList]);

  // Filtered Stock Items (by depot, product, added date & search)
  const filteredStock = useMemo(() => {
    return stockItems.filter((item) => {
      if (selectedDepotId !== 'all' && item.depotId !== selectedDepotId) {
        return false;
      }
      if (selectedProduct !== 'all' && item.skuCode !== selectedProduct && item.sapCode !== selectedProduct) {
        return false;
      }
      if (selectedDate && selectedDate.trim()) {
        const itemAdded = item.createdAt || item.addedDate || '';
        if (!itemAdded.startsWith(selectedDate) && !itemAdded.includes(selectedDate)) {
          return false;
        }
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (
          item.productName.toLowerCase().includes(q) ||
          (item.sapCode && item.sapCode.toLowerCase().includes(q)) ||
          item.skuCode.toLowerCase().includes(q) ||
          (item.batchNumber && item.batchNumber.toLowerCase().includes(q)) ||
          item.depotName.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [stockItems, selectedDepotId, selectedProduct, selectedDate, searchQuery]);

  // Download Sample Excel Template
  const handleDownloadSampleExcel = async () => {
    try {
      await downloadInventoryTemplateAPI();
      showToast('Downloaded stock register template (stock_register_template.xlsx)');
    } catch (err) {
      console.warn('Backend template download failed, using fallback CSV generator:', err);
      const headers = [
        'Depot Name',
        'SAP Code',
        'Product Name',
        'Batch Number',
        'Quantity (Units)',
        'Crates',
        'Mfg Date (YYYY-MM-DD)',
        'Expiry Date (YYYY-MM-DD)',
        'MRP',
      ];
      const sampleRows = [
        [
          'ANAND AGRO-PACKING STATION VASHI (1512)',
          '90100002',
          'GOW. FRESH N-THICK DAHI 400 GM X9.6KG',
          'TEST121',
          '100',
          '9',
          '2026-10-01',
          '2026-12-31',
          '30',
        ],
      ];
      exportToCSV('Inventory_Batch_Upload_Sample', headers, sampleRows);
    }
  };

  // Export current table view
  const handleExportCSV = () => {
    const headers = ['Product Name', 'SAP Code', 'Depot', 'Batch No', 'Available Stock', 'Mfg Date', 'Expiry Date', 'Added Date', 'Status'];
    const rows = filteredStock.map((i) => [
      i.productName,
      i.sapCode || i.skuCode,
      i.depotName,
      i.batchNumber || 'N/A',
      i.currentStock,
      i.mfgDate || 'N/A',
      i.expiryDate || i.earliestExpiry || 'N/A',
      i.createdAt || i.addedDate || 'N/A',
      i.currentStock > 0 ? 'In Stock' : 'Out of Stock',
    ]);
    exportToCSV(selectedDate ? `Stock_List_${selectedDate}` : 'Stock_List_All', headers, rows);
  };

  // Upload Excel Batch Submission (Triggered after user reviews pre-upload preview)
  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) {
      alert('Please select an Excel or CSV file to upload.');
      return;
    }

    const fileName = uploadFile.name;

    try {
      setUploading(true);
      const res = await uploadInventoryBulkAPI(uploadFile);
      if (res?.status === 'success' || res?.message) {
        showToast(res.message || `Excel Batch "${fileName}" imported successfully!`);
      } else {
        showToast(`Excel Batch "${fileName}" uploaded successfully!`);
      }
      fetchStockRegister();
      setIsUploadOpen(false);
      setUploadFile(null);
      setPreviewRows([]);
    } catch (err: any) {
      console.error('Upload API call error:', err);
      showToast(err?.message || 'Failed to process inventory bulk upload');
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadModal = () => {
    setIsUploadOpen(false);
    setUploadFile(null);
    setPreviewRows([]);
    setParseError(null);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-50 bg-slate-900 text-white px-4 py-3 rounded-2xl shadow-xl flex items-center gap-2 border border-slate-700 animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <span className="text-xs font-semibold">{toastMessage}</span>
        </div>
      )}

      {/* 1. Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
              Inventory Management
            </h1>
            <p className="text-xs text-slate-500">
              Manage stock by depot and upload daily batches.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => {
              setIsUploadOpen(true);
              setUploadFile(null);
              setPreviewRows([]);
              setParseError(null);
            }}
            className="px-4 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl shadow-sm flex items-center gap-2 cursor-pointer transition-all active:scale-95"
          >
            <Upload className="w-4 h-4" />
            <span>Upload Excel Batch</span>
          </button>

          <button
            onClick={handleDownloadSampleExcel}
            className="px-3.5 py-2.5 bg-amber-50 hover:bg-amber-100/80 border border-amber-300 text-amber-900 text-xs font-bold rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer transition-all"
          >
            <Download className="w-4 h-4 text-amber-700" />
            <span>Download Template</span>
          </button>

          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* 2. Top Filter Bar (Depot, Product & Date) */}
      <div className="bg-white py-4 px-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
        {/* Depot Filter */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600 mb-1">
            <Building2 className="w-3.5 h-3.5 text-[#C1272D]" />
            <span>Depot</span>
          </div>
          <CustomDropdown
            placeholder="Select Depot"
            options={depoOptions}
            value={selectedDepoName}
            onChange={(valName: string) => {
              if (valName === 'All Depots') {
                setSelectedDepotId('all');
              } else {
                const found = deposList.find((d) => {
                  const label = d.sapCode ? `${d.name} (${d.sapCode})` : d.name;
                  return label === valName || d.name === valName;
                });
                setSelectedDepotId(found ? found.id : 'all');
              }
            }}
          />
        </div>

        {/* Product Filter */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600 mb-1">
            <Package className="w-3.5 h-3.5 text-slate-500" />
            <span>Product</span>
          </div>
          <CustomDropdown
            placeholder="Select Product"
            options={productOptions}
            value={selectedProductName}
            onChange={(valName: string) => {
              if (valName === 'All Products') {
                setSelectedProduct('all');
              } else {
                const found = productsList.find((p) => (p.name || p.displayName) === valName);
                if (found) {
                  setSelectedProduct(found.sku_code || found.sapCode || found.name);
                } else {
                  const stockFound = stockItems.find((s) => s.productName === valName);
                  setSelectedProduct(stockFound ? stockFound.skuCode : valName);
                }
              }
            }}
          />
        </div>

        {/* Date Filter */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600 mb-1">
            <Calendar className="w-3.5 h-3.5 text-slate-500" />
            <span>Date</span>
          </div>
          <CustomCalendar
            placeholder="Select Date"
            value={selectedDate}
            onChange={(dateStr: string) => setSelectedDate(dateStr || '')}
            disablePast={false}
          />
        </div>
      </div>

      {/* 3. Stock Table Container */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Table Header & Search */}
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="px-4 py-2 rounded-xl text-xs font-extrabold bg-[#C1272D] text-white shadow-sm flex items-center gap-1.5">
              <span>📦 Current Stock</span>
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-[11px]">{filteredStock.length}</span>
            </span>
          </div>

          {/* Search Bar */}
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
            <input
              type="text"
              placeholder="Search stock items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 focus:outline-none focus:border-[#C1272D]"
            />
          </div>
        </div>

        {/* Current Stock Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                <th className="py-3.5 px-5">Product Name</th>
                <th className="py-3.5 px-4">SAP Code</th>
                <th className="py-3.5 px-4">Depot</th>
                <th className="py-3.5 px-4">Batch No</th>
                <th className="py-3.5 px-4 text-right">Available Stock</th>
                <th className="py-3.5 px-4">Mfg Date</th>
                <th className="py-3.5 px-4">Expiry Date</th>
                <th className="py-3.5 px-4 whitespace-nowrap">Added Date</th>
                <th className="py-3.5 px-5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
              {loadingStock ? (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-slate-400 font-semibold animate-pulse">
                    Loading inventory stock data...
                  </td>
                </tr>
              ) : filteredStock.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-slate-400">
                    No stock items found.
                  </td>
                </tr>
              ) : (
                filteredStock.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3.5 px-5 font-bold text-slate-800">
                      {item.productName}
                    </td>
                    <td className="py-3.5 px-4 font-mono font-semibold text-slate-600">
                      {item.sapCode || item.skuCode}
                    </td>
                    <td className="py-3.5 px-4 font-medium text-slate-700">
                      {item.depotName}
                    </td>
                    <td className="py-3.5 px-4 font-mono font-bold text-slate-700">
                      <span className="px-2 py-0.5 text-[11px]">
                        {item.batchNumber || 'N/A'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right font-extrabold text-slate-700">
                      {item.currentStock.toLocaleString()}
                    </td>
                    <td className="py-3.5 px-4 font-medium text-slate-700">
                      {item.mfgDate || 'N/A'}
                    </td>
                    <td className="py-3.5 px-4 font-semibold text-slate-800">
                      {item.expiryDate || item.earliestExpiry || 'N/A'}
                    </td>
                    <td className="py-3.5 px-4 font-medium text-slate-700 whitespace-nowrap">
                      {item.createdAt || item.addedDate || 'N/A'}
                    </td>
                    <td className="py-3.5 px-5 text-center">
                      {item.currentStock > 0 ? (
                        <span className="text-emerald-700 rounded-full font-bold text-[10px]">
                          In Stock
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 bg-amber-50 text-amber-800 rounded-full font-bold text-[10px] border border-amber-200">
                          Out of Stock
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Excel Batch Upload & Live Pre-Upload Preview Modal */}
      {isUploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
          <div className={`bg-white rounded-3xl border border-slate-200 shadow-2xl w-full ${previewRows.length > 0 ? 'max-w-4xl' : 'max-w-lg'} overflow-hidden transition-all duration-200 flex flex-col max-h-[90vh]`}>
            {/* Modal Header */}
            <div className="px-6 py-4 bg-red-50/70 border-b border-slate-200 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <FileSpreadsheet className="w-5 h-5 text-[#C1272D]" />
                <div>
                  <h3 className="text-base font-extrabold text-slate-800">
                    {previewRows.length > 0 ? 'Review & Upload Excel Batch' : 'Upload Excel Batch'}
                  </h3>
                  {previewRows.length > 0 && (
                    <p className="text-[11px] text-slate-500 font-medium">
                      Review parsed records below before importing into live inventory.
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleCloseUploadModal}
                className="w-7 h-7 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 flex items-center justify-center cursor-pointer transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleUploadSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                  className="hidden"
                />

                {/* Case A: No file chosen yet */}
                {!uploadFile && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700">
                        Select Excel or CSV File *
                      </label>
                      <button
                        type="button"
                        onClick={handleDownloadSampleExcel}
                        className="text-[11px] font-bold text-[#C1272D] hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        <Download className="w-3 h-3" />
                        Download Template
                      </button>
                    </div>

                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 hover:border-[#C1272D] bg-slate-50/50 hover:bg-red-50/20 rounded-2xl p-8 text-center cursor-pointer transition-colors"
                    >
                      <Upload className="w-8 h-8 mx-auto text-[#C1272D] mb-2" />
                      <p className="text-sm font-bold text-slate-700">Click to choose Excel / CSV file</p>
                      <p className="text-xs text-slate-400 mt-1">Supports standard stock register files (.xlsx, .xls, .csv)</p>
                    </div>
                  </div>
                )}

                {/* Case B: File Selected & Parsing/Previewing */}
                {uploadFile && (
                  <div className="space-y-4">
                    {/* Selected File Info Banner */}
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-[#C1272D] shrink-0 font-bold">
                          <FileSpreadsheet className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs font-extrabold text-slate-800 break-all">{uploadFile.name}</div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                            <span>{(uploadFile.size / 1024).toFixed(1)} KB</span>
                            <span>•</span>
                            <span className="font-semibold text-emerald-700">
                              {parsingFile ? 'Parsing...' : `${previewRows.length} records detected`}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 self-end sm:self-center">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-bold cursor-pointer transition-colors shadow-2xs"
                        >
                          Change File
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFileSelect(null)}
                          className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 cursor-pointer transition-colors"
                          title="Remove file"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Parsing Spinner */}
                    {parsingFile && (
                      <div className="py-8 text-center text-slate-500 animate-pulse text-xs font-bold">
                        Parsing Excel sheet records for preview...
                      </div>
                    )}

                    {/* Parse Error Alert (if any) */}
                    {parseError && (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl text-xs flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-bold">Preview Notice:</p>
                          <p className="text-[11px]">{parseError}</p>
                        </div>
                      </div>
                    )}

                    {/* Pre-Upload Live Preview Table */}
                    {!parsingFile && previewRows.length > 0 && (
                      <div className="space-y-2">
                        {/* Summary Badges Bar */}
                        <div className="flex items-center justify-between px-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-extrabold text-slate-700">Live File Preview</span>
                            <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-[10px] font-extrabold">
                              {previewRows.length} items ready
                            </span>
                          </div>
                          <span className="text-xs text-slate-500 font-semibold">
                            Total Quantity: <strong className="text-slate-800">{previewRows.reduce((acc, r) => acc + r.quantity, 0).toLocaleString()} Units</strong>
                          </span>
                        </div>

                        {/* Table */}
                        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
                          <div className="max-h-72 overflow-y-auto overflow-x-auto">
                            <table className="w-full text-left border-collapse text-xs">
                              <thead>
                                <tr className="bg-[#FFFBEB] text-[11px] font-bold text-slate-700 border-b border-amber-100 uppercase tracking-wider sticky top-0 z-10">
                                  <th className="py-2.5 px-3">#</th>
                                  <th className="py-2.5 px-3">Depot</th>
                                  <th className="py-2.5 px-3">Product Name</th>
                                  <th className="py-2.5 px-3">SAP Code</th>
                                  <th className="py-2.5 px-3">Batch No</th>
                                  <th className="py-2.5 px-3 text-right">Quantity</th>
                                  <th className="py-2.5 px-3">Mfg Date</th>
                                  <th className="py-2.5 px-3">Expiry Date</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 text-slate-700">
                                {previewRows.map((row) => (
                                  <tr key={row.rowNumber} className="hover:bg-slate-50/80 transition-colors">
                                    <td className="py-2.5 px-3 font-semibold text-slate-500 text-[11px]">
                                      {row.rowNumber}
                                    </td>
                                    <td className="py-2.5 px-3 font-medium text-slate-800">
                                      {row.depotName}
                                    </td>
                                    <td className="py-2.5 px-3 font-bold text-slate-800">
                                      {row.productName}
                                    </td>
                                    <td className="py-2.5 px-3 font-mono font-semibold text-slate-600">
                                      {row.sapCode || 'N/A'}
                                    </td>
                                    <td className="py-2.5 px-3 font-mono font-bold text-slate-700">
                                      <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[11px] border border-slate-200">
                                        {row.batchNumber}
                                      </span>
                                    </td>
                                    <td className="py-2.5 px-3 text-right font-extrabold text-slate-800">
                                      {row.quantity.toLocaleString()}
                                    </td>
                                    <td className="py-2.5 px-3 font-medium text-slate-700 text-[11px]">
                                      {row.mfgDate}
                                    </td>
                                    <td className="py-2.5 px-3 font-semibold text-slate-800 text-[11px]">
                                      {row.expiryDate}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Footer Actions */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
                <button
                  type="button"
                  onClick={handleCloseUploadModal}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl cursor-pointer transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!uploadFile || uploading || parsingFile}
                  className="px-5 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl shadow-sm cursor-pointer transition-all flex items-center gap-2 active:scale-95"
                >
                  <Upload className="w-4 h-4" />
                  <span>
                    {uploading
                      ? 'Importing Batch...'
                      : previewRows.length > 0
                      ? `Confirm & Upload (${previewRows.length} Items)`
                      : 'Upload & Add to Stock'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;
